import { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { aggregateTeams, revealBlurUnits, revealPixelBlocks, revealProgress, revealTileGrid, slideAnimClass, tileRevealRanks, type LobbyParticipant, type OptionDistribution, type PublicOption, type QuestionView, type RevealPayload, type ScaleAggregate, type ScoreboardRow, type SlideReveal } from '@livequiz/shared';
import { usePresenter, type PresenterState } from './usePresenter.js';
import { makeT, type TFn } from './i18n.js';
import logoFox from './assets/logo-fox.png';

const TContext = createContext<TFn>(makeT('de'));
const useT = (): TFn => useContext(TContext);
import { applyTheme } from './theme.js';
import { MUSIC_BEDS, getMusicBed, getPack, playCountTick, playFanfare, playGo, playReveal, setCustomSounds, setMusicBed, setPack, wantMusic, type MusicBed, type SoundPack } from './sounds.js';
import { burstConfetti } from './confetti.js';
import { SlideElements } from './SlideElements.js';

const SYMBOLS = ['▲', '◆', '●', '■', '★', '⬟'];
const optColor = (i: number) => `var(--opt-${(i % 6) + 1})`;

/** Pro-Folie-Gestaltung als CSS (überschreibt Theme-Tokens lokal). */
function slideVars(style: QuestionView['style']): React.CSSProperties {
  const s = style ?? {};
  const out: Record<string, string> = {};
  if (s.fontDisplay) out['--font-display'] = s.fontDisplay;
  if (s.accent) out['--accent'] = s.accent;
  if (s.textColor) out.color = s.textColor;
  if (s.bgKind === 'color' && s.bgColor) out.background = s.bgColor;
  else if (s.bgKind === 'image' && s.bgImage) out.background = `center/cover no-repeat url("${s.bgImage}")`;
  return out as React.CSSProperties;
}

/** Wurzel-Style einer Folie inkl. Container für cqw-skalierte Overlay-Elemente. */
function slideRootStyle(style: QuestionView['style']): React.CSSProperties {
  return { ...slideVars(style), containerType: 'size' } as React.CSSProperties;
}

/**
 * Fragetext: frei platziert (style.titleLayout) oder im normalen Fluss.
 * Der Eltern-Container muss `relative` + `container-type:size` haben.
 */
function PromptTitle({ q, className, style }: { q: QuestionView; className?: string; style?: React.CSSProperties }) {
  const tl = q.style?.titleLayout;
  if (tl) {
    return (
      <div
        className={`absolute lq-display lq-rise font-extrabold ${className ?? ''}`}
        style={{
          left: `${tl.x}%`,
          top: `${tl.y}%`,
          width: `${tl.w}%`,
          fontSize: `${tl.fontSize ?? 8}cqw`,
          fontFamily: tl.fontDisplay || undefined,
          color: tl.color || undefined,
          lineHeight: 1.05,
          textAlign: q.style?.align ?? 'center',
          whiteSpace: 'pre-wrap',
          ...style,
        }}
      >
        {q.prompt}
      </div>
    );
  }
  return <h1 className={className} style={{ textAlign: q.style?.align ?? 'center', ...style }}>{q.prompt}</h1>;
}

/**
 * 16:9-Bühne: zentriert und letterboxed in der verfügbaren Fläche. Frei
 * platzierte Titel und Overlay-Elemente (Positionen in %, Schriftgrößen in cqw)
 * sitzen dadurch exakt wie in der Editor-Vorschau – die ebenfalls auf einer
 * 16:9-Fläche mit `container-type: size` gestaltet –, unabhängig vom
 * Seitenverhältnis des Beamers/Fensters. `cqw`/`cqh` beziehen sich auf diese Box.
 */
function Stage({ style, anim, slideKey, children }: { style: QuestionView['style']; anim?: string; slideKey?: React.Key; children: React.ReactNode }) {
  return (
    <div className="w-full h-full flex items-center justify-center overflow-hidden" style={{ containerType: 'size' }}>
      <div
        key={slideKey}
        className={`relative overflow-hidden ${anim ?? ''}`}
        style={{
          width: 'min(100cqw, calc(100cqh * 16 / 9))',
          aspectRatio: '16 / 9',
          containerType: 'size',
          ...slideVars(style),
        }}
      >
        {children}
      </div>
    </div>
  );
}

function getSessionId(): string | null {
  return new URLSearchParams(location.search).get('session');
}

export function App() {
  const sessionId = getSessionId();
  const p = usePresenter(sessionId);
  const t = useMemo(() => makeT(p.locale), [p.locale]);
  const [scale, setScale] = useState(100);
  const [picking, setPicking] = useState(false);

  useEffect(() => {
    document.documentElement.style.fontSize = `${scale}%`;
  }, [scale]);

  // Theme des Quiz anwenden.
  useEffect(() => {
    applyTheme(p.themeId, p.themeOverrides);
  }, [p.themeId, p.themeOverrides]);

  // Eigene Sound-Dateien registrieren.
  useEffect(() => {
    setCustomSounds(p.sounds);
  }, [p.sounds]);

  // Sounds: Countdown-Ticks (3-2-1), Los-Ton, Gong, Fanfare + Hintergrundmusik.
  const prevCountdown = useRef<number | null>(null);
  useEffect(() => {
    if (p.countdown != null && p.countdown !== prevCountdown.current) playCountTick(p.countdown);
    prevCountdown.current = p.countdown;
  }, [p.countdown]);
  const prevPhase = useRef<string | null>(null);
  useEffect(() => {
    if (p.phase !== prevPhase.current) {
      if (p.phase === 'answering') playGo();
      if (p.phase === 'revealed') playReveal();
      // Musik während einer aktiven Frage, sonst aus.
      wantMusic(p.phase === 'countdown' || p.phase === 'answering');
    }
    prevPhase.current = p.phase;
  }, [p.phase]);
  // Ende: Fanfare + Konfetti.
  const prevState = useRef<string | null>(null);
  useEffect(() => {
    if (p.state === 'ended' && prevState.current !== 'ended') {
      wantMusic(false);
      playFanfare();
      burstConfetti();
    }
    prevState.current = p.state;
  }, [p.state]);

  // Tastatursteuerung.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === ' ') {
        e.preventDefault();
        if (p.state === 'lobby') p.act('host:start');
        else p.act('host:next');
      } else if (e.key === 'Escape') {
        p.act(p.paused ? 'host:resume' : 'host:pause');
      } else if (e.key === '+') setScale((s) => Math.min(160, s + 10));
      else if (e.key === '-') setScale((s) => Math.max(70, s - 10));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [p]);

  if (!sessionId) return <Fullscreen><p className="text-2xl lq-muted">{t('noSessionParam')}</p></Fullscreen>;
  if (p.error) return <Fullscreen><p className="text-2xl" style={{ color: 'var(--wrong)' }}>{p.error}</p></Fullscreen>;
  if (!p.ready) return <Fullscreen><p className="text-2xl lq-muted">{t('connecting')}</p></Fullscreen>;

  const isContribution = p.question && p.question.type === 'open_text';
  let body: React.ReactNode;
  if (p.state === 'ended') body = <PodiumView p={p} />;
  else if (p.phase === 'countdown') body = <CountdownView p={p} />;
  else if (isContribution && (p.phase === 'answering' || p.phase === 'revealed')) body = <ContributionView p={p} />;
  else if (p.phase === 'answering' && p.question) body = <QuestionScreen p={p} />;
  else if (p.phase === 'revealed' && p.question && p.reveal) body = <RevealView q={p.question} reveal={p.reveal} />;
  else if (p.phase === 'scoreboard') body = <ScoreboardView p={p} />;
  else body = <LobbyView p={p} />;

  return (
    <TContext.Provider value={t}>
    <div className="h-screen flex flex-col">
      {p.paused && (
        <div className="absolute inset-0 z-40 bg-black/70 flex items-center justify-center">
          <div className="text-6xl font-extrabold lq-display" style={{ color: '#fff' }}>{t('paused')}</div>
        </div>
      )}
      {p.themeOverrides?.logo && p.state !== 'lobby' && (
        <img src={p.themeOverrides.logo} alt="" className="fixed top-3 right-4 z-40 max-h-14 max-w-[18vw] object-contain pointer-events-none lq-fade" />
      )}
      <div className="flex-1 overflow-hidden">{body}</div>
      <FloatingReactions reactions={p.reactions} />
      <ReactionTally counts={p.reactionCounts} />
      {picking && <RandomPicker participants={p.lobby} onClose={() => setPicking(false)} />}
      <ControlBar p={p} scale={scale} setScale={setScale} onRandom={() => setPicking(true)} />
    </div>
    </TContext.Provider>
  );
}

/** Kompakte Gesamtzähler der Reaktionen (unten rechts, wie bei AhaSlides). */
function ReactionTally({ counts }: { counts: Record<string, number> }) {
  const entries = Object.entries(counts).filter(([, n]) => n > 0);
  if (entries.length === 0) return null;
  const total = entries.reduce((s, [, n]) => s + n, 0);
  // Nach Häufigkeit sortiert, die Top-Emojis zuerst.
  entries.sort((a, b) => b[1] - a[1]);
  return (
    <div className="fixed bottom-16 right-3 z-40 flex items-center gap-2 rounded-full bg-black/25 backdrop-blur px-3 py-1 text-sm" style={{ color: '#fff' }} aria-label={`${total} Reaktionen`}>
      {entries.slice(0, 5).map(([emoji, n]) => (
        <span key={emoji} className="flex items-center gap-0.5">
          <span>{emoji}</span>
          <span className="tabular-nums opacity-80">{n}</span>
        </span>
      ))}
    </div>
  );
}

/** Live-Emoji-Reaktionen der Teilnehmer, die vom unteren Rand nach oben schweben. */
function FloatingReactions({ reactions }: { reactions: { emoji: string; id: string }[] }) {
  return (
    <div className="pointer-events-none fixed inset-0 z-50 overflow-hidden" aria-hidden>
      <style>{`@keyframes lqFloat{0%{transform:translateY(0) scale(.6);opacity:0}12%{opacity:1}100%{transform:translateY(-72vh) scale(1.25);opacity:0}}`}</style>
      {reactions.map((r) => {
        // Horizontale Position deterministisch aus der id (kein Layout-Sprung bei Re-Render).
        const left = (parseInt(r.id.slice(0, 4), 16) % 92) + 4;
        const dur = 2.4 + (parseInt(r.id.slice(4, 6), 16) % 10) / 10;
        return (
          <span
            key={r.id}
            style={{ position: 'absolute', bottom: '6rem', left: `${left}%`, fontSize: '3.2rem', animation: `lqFloat ${dur}s ease-out forwards` }}
          >
            {r.emoji}
          </span>
        );
      })}
    </div>
  );
}

function Fullscreen({ children }: { children: React.ReactNode }) {
  return <div className="h-screen flex items-center justify-center">{children}</div>;
}

function Logo({ p }: { p: PresenterState }) {
  const url = p.themeOverrides?.logo;
  // Eigenes Quiz-Logo hat Vorrang; sonst die quIZz-Marke zeigen.
  if (url) return <img src={url} alt="" className="max-h-20 mb-4 lq-fade" />;
  return (
    <div className="flex items-center gap-4 mb-4 lq-fade">
      <img src={logoFox} alt="quIZz" width={72} height={72} draggable={false} />
      <div className="text-5xl font-extrabold lq-display leading-none">
        <span style={{ color: '#fff' }}>qu</span><span style={{ color: 'var(--accent)' }}>IZ</span><span style={{ color: '#fff' }}>z</span>
      </div>
    </div>
  );
}

// ─── Lobby ───────────────────────────────────────────────────────────────────

function QR({ url }: { url: string }) {
  const [src, setSrc] = useState<string>('');
  useEffect(() => {
    QRCode.toDataURL(url, { width: 360, margin: 1, color: { dark: '#0b1020', light: '#ffffff' } }).then(setSrc).catch(() => setSrc(''));
  }, [url]);
  return src ? <img src={src} alt="QR-Code zum Beitreten" className="rounded-2xl bg-white p-3 lq-fade" width={280} height={280} /> : null;
}

/** Stabiler String-Hash – für Größen-/Farbvariation je Teilnehmer in der Wortwolke. */
function nameHash(s: string): number {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function LobbyView({ p }: { p: PresenterState }) {
  const t = useT();
  const n = p.lobby.length;
  // Grundgröße der Wortwolke sinkt mit der Teilnehmerzahl, damit alles ins Bild passt.
  const baseRem = n > 140 ? 1.0 : n > 80 ? 1.3 : n > 40 ? 1.7 : n > 15 ? 2.1 : 2.6;
  // Bei englischer Session die Beitritts-Links/QR mit ?lang=en versehen, damit
  // auch die Beitritts-Seite auf dem Handy von Anfang an englisch ist.
  const joinUrl = p.joinUrl ? (p.locale === 'en' ? p.joinUrl + (p.joinUrl.includes('?') ? '&' : '?') + 'lang=en' : p.joinUrl) : null;
  return (
    <div className="h-full flex flex-col items-center px-10 py-6 overflow-hidden">
      {/* Kopf: Logo, PIN, QR – immer sichtbar (shrink-0) */}
      <div className="shrink-0 flex flex-col items-center gap-3">
        <Logo p={p} />
        <h1 className="text-3xl font-bold lq-muted lq-display">{t('joinUnder')}</h1>
        <div className="flex items-center gap-12">
          <div className="text-center">
            <div className="lq-muted text-xl mb-1">{t('joinPin')}</div>
            <div className="text-7xl font-black tracking-widest tabular-nums lq-display">{p.pin}</div>
            {joinUrl && <div className="mt-2 text-lg lq-muted">{joinUrl.replace(/^https?:\/\//, '')}</div>}
          </div>
          {joinUrl && <QR url={joinUrl} />}
        </div>
      </div>

      {/* Teilnehmerzahl – gut sichtbar */}
      <div className="shrink-0 mt-4 mb-1 flex items-baseline gap-2">
        <span key={p.total} className="text-5xl font-black tabular-nums lq-display lq-pop" style={{ color: 'var(--accent)' }}>{p.total}</span>
        <span className="text-2xl lq-muted">{p.total === 1 ? t('participantOne') : t('participantMany')}</span>
      </div>

      {/* Namen als aufpoppende Wortwolke – füllt den Rest, läuft nie über (overflow-hidden). */}
      <div className="flex-1 min-h-0 w-full overflow-hidden flex flex-wrap items-center content-center justify-center gap-x-6 gap-y-2">
        {n === 0 ? (
          <p className="lq-muted text-2xl">{t('waitingParticipants')}</p>
        ) : (
          p.lobby.map((x) => {
            const h = nameHash(x.id);
            const size = (baseRem * (0.72 + (h % 45) / 100)).toFixed(2);
            return (
              <span key={x.id} className="lq-pop lq-display font-extrabold leading-none" style={{ fontSize: `${size}rem`, color: optColor(h) }}>
                {x.emoji} {x.nickname}
              </span>
            );
          })
        )}
      </div>

      {/* Hinweis – immer sichtbar */}
      <p className="shrink-0 mt-2 lq-muted opacity-70">{t('spaceToStart')}</p>
    </div>
  );
}

// ─── Countdown ───────────────────────────────────────────────────────────────

function CountdownView({ p }: { p: PresenterState }) {
  const t = useT();
  return (
    <div className="h-full flex flex-col items-center justify-center">
      <div className="lq-muted text-3xl mb-4 lq-display">{t('nextQuestion')}</div>
      <div key={p.countdown} className="text-[12rem] font-black leading-none lq-display lq-count" style={{ color: 'var(--accent)' }}>
        {p.countdown ?? ''}
      </div>
    </div>
  );
}

// ─── Frage / Antwortphase ────────────────────────────────────────────────────

function useRemaining(endsAt: number | null, clockOffset: number): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 200);
    return () => clearInterval(t);
  }, []);
  if (!endsAt) return 0;
  return Math.max(0, endsAt - (now + clockOffset));
}

function QuestionScreen({ p }: { p: PresenterState }) {
  const t = useT();
  const q = p.question!;
  const remaining = useRemaining(p.endsAt, p.clockOffset);
  const pct = q.timeLimitS > 0 ? Math.max(0, Math.min(100, (remaining / (q.timeLimitS * 1000)) * 100)) : 0;
  const inputAtDevice = ['numeric_estimate', 'free_text', 'image_pin'].includes(q.type);

  // Infofolie: ruhige Ansicht ohne Timer/Zähler/Optionen, auf 16:9-Bühne gerendert,
  // damit frei platzierte Titel und Overlay-Elemente exakt wie im Editor sitzen.
  if (q.type === 'info') {
    const hasTL = Boolean(q.style?.titleLayout);
    return (
      <Stage style={q.style} anim={slideAnimClass(q.style?.anim)} slideKey={q.index}>
        <div className="absolute inset-0 flex flex-col" style={{ padding: '4cqw', textAlign: q.style?.align ?? 'center' }}>
          {!hasTL && (
            <h1 className="lq-display lq-rise" style={{ fontSize: '6cqw', fontWeight: 800, lineHeight: 1.05, margin: 0, marginBottom: '2.2cqw', whiteSpace: 'pre-wrap' }}>
              {q.prompt}
            </h1>
          )}
          <div style={{ flex: 1, minHeight: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '2.2cqw' }}>
            {q.video?.src ? (
              <div style={{ flex: '0 0 auto', minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <video
                  key={q.video.src}
                  src={q.video.src}
                  className="lq-fade"
                  style={{ maxHeight: '48cqw', maxWidth: '100%', width: 'auto', borderRadius: '2cqw', background: '#000' }}
                  controls
                  autoPlay
                  loop={q.video.loop}
                  muted={q.video.muted}
                  playsInline
                />
              </div>
            ) : (
              q.imagePath && (
                <div style={{ flex: '0 0 auto', minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <img src={q.imagePath} alt="" className="lq-fade" style={{ maxHeight: '36cqw', maxWidth: '100%', width: 'auto', height: 'auto', objectFit: 'contain', borderRadius: '2cqw' }} />
                </div>
              )
            )}
          </div>
        </div>
        {hasTL && <PromptTitle q={q} />}
        <SlideElements elements={q.style?.elements} />
      </Stage>
    );
  }

  // Glücksrad-Folie: Rad mit Einträgen (oder Teilnehmenden), Host dreht.
  if (q.type === 'spinner') {
    const sp = q.spinner ?? { entries: [], useParticipants: false };
    const entries = sp.useParticipants ? p.lobby.map((x) => `${x.emoji} ${x.nickname}`) : sp.entries;
    return (
      <div className={`relative h-full flex flex-col items-center justify-center gap-8 px-16 ${slideAnimClass(q.style?.anim)}`} key={q.index} style={slideRootStyle(q.style)}>
        <PromptTitle q={q} className="text-5xl font-extrabold lq-display" />
        <SpinnerView entries={entries} />
        <SlideElements elements={q.style?.elements} />
      </div>
    );
  }

  const hasImage = Boolean(q.imagePath) && q.type !== 'image_pin';
  return (
    <div className={`relative h-full flex flex-col px-10 py-6 ${slideAnimClass(q.style?.anim)}`} key={q.index} style={{ ...slideVars(q.style), containerType: 'size' }}>
      <div className="flex items-center justify-between mb-3" style={p.themeOverrides?.logo ? { paddingRight: '7rem' } : undefined}>
        <div className="text-2xl lq-muted">{t('questionHeader', { i: q.index + 1, n: q.total })}</div>
        <div className="flex items-center gap-6">
          <div className="text-3xl font-bold tabular-nums">{Math.ceil(remaining / 1000)}s</div>
          <div className="text-2xl lq-muted">{t('answeredCount', { a: p.answered, t: p.total })}</div>
        </div>
      </div>
      <div className="h-3 rounded-full lq-card overflow-hidden mb-5">
        <div className="h-full transition-[width] duration-200" style={{ width: `${pct}%`, background: 'var(--accent)' }} />
      </div>

      <PromptTitle q={q} className="text-5xl font-extrabold leading-tight mb-4 lq-display lq-rise" />
      {q.bonus && (
        <div className="text-center mb-3">
          <span className="inline-flex items-center gap-2 rounded-full px-6 py-2 text-2xl font-black lq-pop" style={{ background: 'var(--accent)', color: 'var(--accent-fg)' }}>{t('bonusRound')}</span>
        </div>
      )}

      {/* Inhaltsbereich füllt die restliche Höhe und ist vertikal zentriert. */}
      <div className="flex-1 min-h-0 flex flex-col justify-center gap-5">
        {hasImage && (q.style?.reveal ? (
          <RevealImage src={q.imagePath!} mode={q.style.reveal} intensity={q.style.revealIntensity} endsAt={p.endsAt} clockOffset={p.clockOffset} timeLimitS={q.timeLimitS} seed={q.index} />
        ) : (
          <SlideImage src={q.imagePath!} big={inputAtDevice} />
        ))}
        {q.type === 'image_pin' ? (
          <PinImage q={q} />
        ) : q.type === 'match_pair' ? (
          <MatchColumns q={q} />
        ) : q.type === 'wordcloud' ? (
          <LiveWordCloud words={p.words} />
        ) : q.type === 'scale' ? (
          <ScaleChart data={p.scaleLive ?? emptyScale(q)} live answered={p.answered} />
        ) : q.type === 'poll' ? (
          <PollBars options={q.options} live={p.pollLive} />
        ) : inputAtDevice ? (
          <div className="text-center text-3xl lq-muted">{t('enterOnDevice')}</div>
        ) : (
          <OptionsGrid options={q.options} type={q.type} />
        )}
      </div>
      <SlideElements elements={q.style?.elements} />
    </div>
  );
}

const SCALE_COLORS = ['#6ea8ff', '#4ade80', '#fb923c', '#38bdf8', '#f472b6', '#c084fc', '#2dd4bf', '#facc15'];

/** Leeres Aggregat (vor der ersten Antwort), damit Skala/Aussagen schon sichtbar sind. */
function emptyScale(q: QuestionView): ScaleAggregate {
  const s = q.scale!;
  const stmts = s.statements.length ? s.statements : [q.prompt || 'Bewertung'];
  const values: { value: number; count: number }[] = [];
  for (let v = s.min; v <= s.max; v++) values.push({ value: v, count: 0 });
  return { min: s.min, max: s.max, statements: stmts.map((label) => ({ label, average: 0, responses: 0, values: values.map((x) => ({ ...x })) })) };
}

/** Live-Diagramm der Skala: je Aussage ein farbiger Balken (Ø-Position auf der Skala). */
function ScaleChart({ data, live, answered }: { data: ScaleAggregate; live?: boolean; answered?: number }) {
  const t = useT();
  const { min, max } = data;
  const multi = data.statements.length > 1;
  return (
    <div className="flex-1 flex flex-col justify-center gap-5 max-w-4xl mx-auto w-full lq-stagger">
      {data.statements.map((st, i) => {
        const color = SCALE_COLORS[i % SCALE_COLORS.length];
        const pct = max > min ? ((st.average - min) / (max - min)) * 100 : 0;
        return (
          <div key={i}>
            <div className="flex justify-between text-2xl mb-1">
              <span className="font-semibold">{multi && <span className="inline-block w-3.5 h-3.5 rounded-full mr-2 align-middle" style={{ background: color }} />}{st.label}</span>
              <span className="tabular-nums"><span className="font-black" style={{ color }}>{st.responses > 0 ? st.average : '–'}</span> <span className="text-lg lq-muted">({st.responses})</span></span>
            </div>
            <div className="h-7 rounded-full lq-card overflow-hidden">
              <div className="h-full transition-[width] duration-300 lq-grow" style={{ width: `${st.responses > 0 ? pct : 0}%`, background: color, transformOrigin: 'left' }} />
            </div>
          </div>
        );
      })}
      <div className="flex justify-between text-lg lq-muted px-1"><span>{min}</span><span>{max}</span></div>
      {live && <div className="text-center lq-muted text-xl">{t('answersLive', { n: answered ?? 0 })}</div>}
    </div>
  );
}

function LiveWordCloud({ words }: { words: { text: string; count: number }[] | null }) {
  const t = useT();
  const items = words ?? [];
  if (items.length === 0) {
    return <div className="flex-1 flex items-center justify-center text-3xl lq-muted">{t('waitInput')}</div>;
  }
  const max = Math.max(...items.map((w) => w.count));
  return (
    <div className="flex-1 flex flex-wrap items-center justify-center gap-x-8 gap-y-4 content-center overflow-hidden">
      {items.map((w, i) => {
        const scale = 1 + (w.count / max) * 2.6;
        return (
          <span key={w.text} className="lq-pop font-extrabold leading-none lq-display" style={{ fontSize: `${scale}rem`, color: optColor(i) }} title={`${w.count}×`}>
            {w.text}
          </span>
        );
      })}
    </div>
  );
}

function MatchColumns({ q }: { q: QuestionView }) {
  return (
    <div className="flex-1 grid grid-cols-2 gap-10 content-center max-w-5xl mx-auto w-full">
      <div className="space-y-3 lq-stagger">
        {q.options.map((o) => (
          <div key={o.id} className="rounded-2xl lq-card lq-border px-6 py-4 text-3xl font-semibold">{o.label}</div>
        ))}
      </div>
      <div className="space-y-3 lq-stagger">
        {(q.rights ?? []).map((r) => (
          <div key={r.id} className="rounded-2xl px-6 py-4 text-3xl text-center" style={{ background: 'var(--accent)', color: 'var(--accent-fg)' }}>
            {r.label}
          </div>
        ))}
      </div>
    </div>
  );
}

function ContributionView({ p }: { p: PresenterState }) {
  const t = useT();
  const q = p.question!;
  const items = p.contributions ?? [];
  return (
    <div className="relative h-full flex flex-col px-10 py-6" style={slideRootStyle(q.style)}>
      <div className="flex items-center gap-3 mb-2">
        <div className="text-2xl lq-muted">{t('questionHeader', { i: q.index + 1, n: q.total })}</div>
        <div className="text-2xl lq-muted">· {t('contribCount', { n: items.length })}</div>
      </div>
      <PromptTitle q={q} className="text-5xl font-extrabold text-center leading-tight mb-6 lq-display" />
      {items.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-3xl lq-muted">{t('waitContrib')}</div>
      ) : (
        <div className="flex-1 overflow-hidden columns-2 gap-4">
          {items.map((c) => (
            <div key={c.id} className="flex items-start gap-3 rounded-2xl lq-card lq-border px-6 py-4 text-2xl mb-4 break-inside-avoid lq-rise">
              <span className="flex-1">{c.text}</span>
              {q.allowUpvotes && (
                <span className="shrink-0 inline-flex items-center gap-1 rounded-full px-3 py-1 text-xl font-bold" style={{ background: 'var(--accent)', color: 'var(--accent-fg)' }}>
                  ▲ {c.votes}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/** Höhendeckel für Enthüllungsbilder auf dem Beamer: definiter vh-Wert, damit
 *  das Bild nie seinen Bereich überläuft und die Antworten darunter frei bleiben. */
const REVEAL_IMG_MAXH = '40vh';

/** Fragebild: groß, zentriert, proportionserhaltend – füllt den verfügbaren
 *  Bereich, ohne den Titel oder die Optionen zu überlagern (flex-fit). */
function SlideImage({ src }: { src: string; big?: boolean }) {
  return (
    <div className="flex-1 min-h-0 w-full flex items-center justify-center">
      <img src={src} alt="" className="max-h-full max-w-full w-auto object-contain rounded-2xl lq-fade" />
    </div>
  );
}

/**
 * Bild-Enthüllung: deckt das Fragebild über die Antwortzeit schrittweise auf –
 * als Weichzeichner (langsam scharf) oder als Kachelraster (Stück für Stück).
 * Vollständig offline (nur CSS + rAF), synchron zur verbleibenden Zeit.
 */
function RevealImage({ src, mode, intensity, endsAt, clockOffset, timeLimitS, seed }: {
  src: string; mode: SlideReveal; intensity?: number; endsAt: number | null; clockOffset: number; timeLimitS: number; seed: number;
}) {
  const progress = useRevealProgress(endsAt, clockOffset, timeLimitS);
  if (mode === 'pixelate') {
    return (
      <div className="flex-1 min-h-0 w-full flex items-center justify-center overflow-hidden">
        <LivePixel src={src} blocks={revealPixelBlocks(intensity, progress)} />
      </div>
    );
  }
  const blurPx = revealBlurUnits(intensity) * 8;
  // Wichtig: definiter vh-Höhendeckel statt `max-h-full`. Prozenthöhen lösen im
  // (inhaltsgroßen) relativen Wrapper nicht auf – das Bild würde sonst in
  // Naturgröße rendern und die Antworten darunter überlagern. Der flex-1-Container
  // teilt sich die Höhe mit den Antworten (kein Überlauf), der vh-Deckel begrenzt
  // das Bild zusätzlich; overflow-hidden fängt Rundungsreste ab.
  return (
    <div className="flex-1 min-h-0 w-full flex items-center justify-center overflow-hidden">
      <div className="relative" style={{ maxWidth: '100%' }}>
        <img
          src={src}
          alt=""
          className="block w-auto object-contain rounded-2xl"
          style={{
            maxHeight: REVEAL_IMG_MAXH,
            maxWidth: '100%',
            ...(mode === 'blur' ? {
              filter: `blur(${(1 - progress) * blurPx}px)`,
              transform: `scale(${1 + (1 - progress) * 0.06})`,
              transition: 'filter 200ms linear, transform 200ms linear',
            } : {}),
          }}
        />
        {mode === 'tiles' && <TileCover progress={progress} seed={seed} intensity={intensity} />}
      </div>
    </div>
  );
}

/** Kachel-Overlay: verdeckte Kacheln verschwinden nacheinander (deterministische Reihenfolge). */
function TileCover({ progress, seed, intensity }: { progress: number; seed: number; intensity?: number }) {
  const { cols, rows } = revealTileGrid(intensity);
  const count = cols * rows;
  const ranks = useMemoTiles(count, seed);
  const revealedCount = Math.floor(progress * count);
  return (
    <div
      className="absolute inset-0 rounded-2xl overflow-hidden grid"
      style={{ gridTemplateColumns: `repeat(${cols}, 1fr)`, gridTemplateRows: `repeat(${rows}, 1fr)`, gap: '2px', pointerEvents: 'none' }}
    >
      {ranks.map((rank, i) => (
        <div
          key={i}
          style={{
            background: 'var(--card, #10162a)',
            opacity: rank >= revealedCount ? 1 : 0,
            transition: 'opacity 380ms ease',
          }}
        />
      ))}
    </div>
  );
}

/** Canvas-Mosaik: kleine Version per Nearest-Neighbor hochskaliert (Verpixelung). */
function LivePixel({ src, blocks }: { src: string; blocks: number }) {
  const ref = useRef<HTMLCanvasElement>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  function draw() {
    const c = ref.current, im = imgRef.current;
    if (!c || !im || !im.naturalWidth) return;
    const cw = c.clientWidth, ch = c.clientHeight;
    if (!cw || !ch) return;
    c.width = cw; c.height = ch;
    const ctx = c.getContext('2d'); if (!ctx) return;
    const ar = im.naturalWidth / im.naturalHeight;
    let dw = cw, dh = cw / ar; if (dh > ch) { dh = ch; dw = ch * ar; }
    const dx = (cw - dw) / 2, dy = (ch - dh) / 2;
    const bw = Math.max(2, Math.round(blocks)), bh = Math.max(2, Math.round(blocks / ar));
    ctx.clearRect(0, 0, cw, ch);
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(im, 0, 0, bw, bh);
    ctx.imageSmoothingEnabled = false;
    ctx.drawImage(c, 0, 0, bw, bh, dx, dy, dw, dh);
  }
  useEffect(() => {
    const im = new Image();
    im.onload = () => { imgRef.current = im; draw(); };
    im.src = src;
    const c = ref.current;
    const ro = c ? new ResizeObserver(() => draw()) : null;
    if (c && ro) ro.observe(c);
    return () => ro?.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src]);
  useEffect(() => { draw(); });
  return <canvas ref={ref} className="rounded-2xl" style={{ width: '100%', height: '100%' }} />;
}

function useMemoTiles(count: number, seed: number): number[] {
  const ref = useRef<{ count: number; seed: number; ranks: number[] } | null>(null);
  if (!ref.current || ref.current.count !== count || ref.current.seed !== seed) {
    ref.current = { count, seed, ranks: tileRevealRanks(count, seed) };
  }
  return ref.current.ranks;
}

/** rAF-Fortschritt 0..1 aus der verbleibenden Antwortzeit (endet bei Zeitablauf auf 1). */
function useRevealProgress(endsAt: number | null, clockOffset: number, timeLimitS: number): number {
  const [prog, setProg] = useState(() => (endsAt && timeLimitS > 0 ? 0 : 1));
  useEffect(() => {
    if (!endsAt || timeLimitS <= 0) { setProg(1); return; }
    let raf = 0;
    let last = -1;
    const tick = () => {
      const remaining = Math.max(0, endsAt - (Date.now() + clockOffset));
      const p = revealProgress(remaining, timeLimitS);
      if (Math.abs(p - last) >= 0.008) { last = p; setProg(p); }
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [endsAt, clockOffset, timeLimitS]);
  return prog;
}

/** „Pins auf Bild": das Bild groß und zentral auf dem Beamer, Eingabe am Gerät. */
function PinImage({ q }: { q: QuestionView }) {
  const t = useT();
  if (!q.imagePath) {
    return <div className="flex-1 flex items-center justify-center text-3xl lq-muted">{t('noImage')}</div>;
  }
  return (
    <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-4">
      <div className="flex-1 min-h-0 w-full flex items-center justify-center">
        <img src={q.imagePath} alt="" className="max-h-full max-w-full w-auto object-contain rounded-2xl lq-fade" />
      </div>
      <div className="text-2xl lq-muted shrink-0">{t('tapOnDevice')}</div>
    </div>
  );
}

function OptionsGrid({ options, type }: { options: PublicOption[]; type: QuestionView['type'] }) {
  const cols = options.length <= 2 ? 'grid-cols-1' : 'grid-cols-2';
  if (type === 'ordering' || type === 'ranking') {
    return (
      <div className="flex-1 grid grid-cols-1 gap-3 max-w-4xl mx-auto w-full content-center lq-stagger">
        {options.map((o, i) => (
          <div key={o.id} className="rounded-2xl lq-card lq-border px-6 py-4 text-3xl flex items-center gap-4">
            <span className="lq-muted">{i + 1}</span> {o.label}
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className={`flex-1 grid ${cols} gap-4 content-center lq-stagger`}>
      {options.map((o, i) => (
        <div key={o.id} style={{ backgroundColor: optColor(i), color: 'var(--opt-fg)' }} className="rounded-2xl px-6 py-6 text-3xl font-bold flex items-center gap-4 min-h-[8rem]">
          <span className="text-4xl shrink-0">{SYMBOLS[i % SYMBOLS.length]}</span>
          {o.imagePath && <img src={o.imagePath} alt="" className="max-h-32 rounded-lg object-contain" />}
          {o.label && <span>{o.label}</span>}
        </div>
      ))}
    </div>
  );
}

/** Live-Balkendiagramm einer Umfrage (Stimmen wachsen während der Antwortzeit). */
function PollBars({ options, live }: { options: PublicOption[]; live: OptionDistribution[] | null }) {
  const t = useT();
  // Fallback: Nullwerte aus den Optionen, solange noch keine Stimmen da sind.
  const dist: OptionDistribution[] = live ?? options.map((o) => ({ optionId: o.id, label: o.label, count: 0, isCorrect: false }));
  const total = dist.reduce((s, d) => s + d.count, 0);
  const max = Math.max(1, ...dist.map((d) => d.count));
  return (
    <div className="flex-1 flex flex-col justify-center gap-4 max-w-4xl mx-auto w-full lq-stagger">
      {dist.map((d, i) => {
        const pct = total > 0 ? Math.round((d.count / total) * 100) : 0;
        return (
          <div key={d.optionId} className="flex items-center gap-4">
            <span className="text-3xl shrink-0 w-10 text-center">{SYMBOLS[i % SYMBOLS.length]}</span>
            <div className="flex-1">
              <div className="flex items-center justify-between text-2xl mb-1">
                <span className="font-semibold">{d.label}</span>
                <span className="tabular-nums lq-muted">{d.count} · {pct}%</span>
              </div>
              <div className="h-8 rounded-full lq-card overflow-hidden">
                <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${(d.count / max) * 100}%`, minWidth: d.count > 0 ? '1.5rem' : 0, backgroundColor: optColor(i) }} />
              </div>
            </div>
          </div>
        );
      })}
      <div className="text-center text-xl lq-muted mt-1">{t('votesN', { n: total })}</div>
    </div>
  );
}

// ─── Auflösung ───────────────────────────────────────────────────────────────

function RevealView({ q, reveal }: { q: QuestionView; reveal: RevealPayload }) {
  const t = useT();
  const max = Math.max(1, ...reveal.distribution.map((d) => d.count));
  return (
    <div className="relative h-full flex flex-col px-10 py-6" style={slideRootStyle(q.style)}>
      <PromptTitle q={q} className="text-4xl font-extrabold text-center mb-6 lq-display lq-rise" />
      {reveal.pins ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="relative inline-block">
            {q.imagePath && <img src={q.imagePath} alt="" className="max-h-[60vh] w-auto rounded-2xl" />}
            <span className="absolute rounded-full border-4 border-green-400 -translate-x-1/2 -translate-y-1/2" style={{ left: `${reveal.pins.target.x}%`, top: `${reveal.pins.target.y}%`, width: `${reveal.pins.target.radius * 2}%`, aspectRatio: '1' }} />
            {reveal.pins.points.map((pt, i) => (
              <span key={i} className="absolute w-3.5 h-3.5 rounded-full border border-white/70 -translate-x-1/2 -translate-y-1/2" style={{ left: `${pt.x}%`, top: `${pt.y}%`, background: pt.correct ? '#16a34a' : '#dc2626' }} />
            ))}
          </div>
        </div>
      ) : reveal.ranking ? (
        <div className="flex-1 flex flex-col justify-center max-w-3xl mx-auto w-full lq-stagger">
          {reveal.ranking.map((r, i) => (
            <div key={r.label} className="flex items-center gap-4 rounded-2xl lq-card lq-border px-6 py-4 text-3xl mb-3">
              <span className="font-black lq-display" style={{ color: 'var(--accent)' }}>{i + 1}</span>
              <span className="flex-1">{r.label}</span>
              <span className="text-xl lq-muted">Ø {r.avg}</span>
            </div>
          ))}
        </div>
      ) : reveal.scale ? (
        <ScaleChart data={reveal.scale} />
      ) : reveal.distribution.length > 0 ? (
        <div className="flex-1 flex items-end justify-center gap-6 pb-4">
          {reveal.distribution.map((d: OptionDistribution, i) => (
            <div key={d.optionId} className="flex flex-col items-center justify-end h-full" style={{ width: `${80 / reveal.distribution.length}%` }}>
              <div className="text-2xl mb-1">{d.count}</div>
              <div
                className="w-full rounded-t-xl lq-grow"
                style={{
                  backgroundColor: optColor(i),
                  height: `${(d.count / max) * 100}%`,
                  minHeight: '8px',
                  outline: d.isCorrect ? '4px solid var(--correct)' : 'none',
                  outlineOffset: '2px',
                }}
              />
              <div className="mt-2 text-xl text-center flex items-center gap-2">
                <span>{SYMBOLS[i % SYMBOLS.length]}</span>
                {d.label}
                {d.isCorrect && <span style={{ color: 'var(--correct)' }}>✓</span>}
              </div>
            </div>
          ))}
        </div>
      ) : reveal.words && reveal.words.length > 0 ? (
        <div className="flex-1 flex flex-wrap items-center justify-center gap-4 content-center">
          {reveal.words.map((w, i) => (
            <span key={w.text} style={{ fontSize: `${1 + Math.min(4, w.count) * 0.6}rem`, color: optColor(i) }} className="font-bold lq-display">{w.text}</span>
          ))}
        </div>
      ) : (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-4xl font-bold text-center lq-display" style={{ color: 'var(--correct)' }}>{reveal.correctText}</div>
        </div>
      )}
      {reveal.correctText && reveal.distribution.length > 0 && (
        <div className="text-center text-2xl mb-2" style={{ color: 'var(--correct)' }}>{t('correctLabel', { text: reveal.correctText ?? '' })}</div>
      )}
      {reveal.explanation && (
        <div className="mx-auto max-w-4xl rounded-2xl lq-card lq-border px-6 py-4 text-2xl text-center lq-rise">
          {reveal.explanation}
        </div>
      )}
    </div>
  );
}

// ─── Scoreboard / Podium ─────────────────────────────────────────────────────

function ScoreboardView({ p }: { p: PresenterState }) {
  const t = useT();
  const rows = p.scoreboard ?? [];
  // Team-Modus: aggregierte Teamwertung anzeigen.
  if (p.teams && p.teams.length > 0) {
    const teamRows = aggregateTeams(rows, p.teams);
    const tmax = Math.max(1, ...teamRows.map((x) => x.score));
    return (
      <div className="h-full flex flex-col items-center justify-center px-16">
        <h1 className="text-5xl font-extrabold mb-8 lq-display">{t('teamStandings')}</h1>
        <div className="w-full max-w-4xl space-y-4 lq-stagger">
          {teamRows.map((t) => (
            <div key={t.team} className="flex items-center gap-4">
              <div className="w-10 text-2xl lq-muted text-right">{t.rank}</div>
              <div className="flex-1">
                <div className="flex justify-between text-3xl mb-1">
                  <span className="font-bold">{t.team} <span className="text-lg lq-muted">({t.members})</span></span>
                  <span className="tabular-nums">{t.score}</span>
                </div>
                <div className="h-4 rounded-full lq-card overflow-hidden">
                  <div className="h-full lq-grow" style={{ width: `${(t.score / tmax) * 100}%`, background: 'var(--accent)', transformOrigin: 'left' }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  return (
    <div className="h-full flex flex-col items-center justify-center px-16">
      <h1 className="text-5xl font-extrabold mb-8 lq-display">{t('standings')}</h1>
      <Leaderboard rows={rows} />
    </div>
  );
}

/** Rangliste mit FLIP-Animation: Zeilen gleiten sichtbar auf ihre neue Position. */
function Leaderboard({ rows }: { rows: ScoreboardRow[] }) {
  const max = Math.max(1, ...rows.map((r) => r.score));
  const refs = useRef(new Map<string, HTMLDivElement>());
  const prevTops = useRef(new Map<string, number>());
  const shown = rows.slice(0, 8);

  useLayoutEffect(() => {
    for (const [pid, el] of refs.current) {
      const newTop = el.getBoundingClientRect().top;
      const oldTop = prevTops.current.get(pid);
      if (oldTop != null && Math.abs(oldTop - newTop) > 1) {
        el.style.transition = 'none';
        el.style.transform = `translateY(${oldTop - newTop}px)`;
        requestAnimationFrame(() => {
          el.style.transition = 'transform 650ms cubic-bezier(.2,.8,.2,1)';
          el.style.transform = '';
        });
      }
      prevTops.current.set(pid, newTop);
    }
  });

  return (
    <div className="w-full max-w-4xl space-y-3">
      {shown.map((r) => (
        <div
          key={r.participantId}
          ref={(el) => { if (el) refs.current.set(r.participantId, el); else refs.current.delete(r.participantId); }}
          className="flex items-center gap-4 rounded-xl lq-card px-3 py-2"
        >
          <div className="w-10 text-2xl lq-muted text-right tabular-nums">{r.rank}</div>
          <div className="flex-1">
            <div className="flex justify-between text-2xl mb-1">
              <span className="font-semibold"><span className="mr-2">{r.emoji}</span>{r.nickname}</span>
              <span className="tabular-nums">{r.score}{r.delta > 0 && <span className="text-lg" style={{ color: 'var(--correct)' }}> +{r.delta}</span>}</span>
            </div>
            <div className="h-3 rounded-full lq-card overflow-hidden">
              <div className="h-full lq-grow" style={{ width: `${(r.score / max) * 100}%`, background: 'var(--accent)', transformOrigin: 'left' }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function PodiumView({ p }: { p: PresenterState }) {
  const t = useT();
  const rows = p.podium ?? [];
  // Team-Modus: Team-Podium.
  if (p.teams && p.teams.length > 0) {
    const teamRows = aggregateTeams(rows, p.teams);
    const tmax = Math.max(1, ...teamRows.map((x) => x.score));
    return (
      <div className="h-full flex flex-col items-center justify-center gap-8 px-16">
        <h1 className="text-6xl font-black lq-display">{t('teamFinal')}</h1>
        <div className="w-full max-w-3xl space-y-4 lq-stagger">
          {teamRows.map((t) => (
            <div key={t.team} className="flex items-center gap-4">
              <div className="w-12 text-3xl lq-muted text-right">{t.rank === 1 ? '🥇' : t.rank === 2 ? '🥈' : t.rank === 3 ? '🥉' : t.rank}</div>
              <div className="flex-1">
                <div className="flex justify-between text-3xl mb-1">
                  <span className="font-bold">{t.team} <span className="text-lg lq-muted">({t.members})</span></span>
                  <span className="tabular-nums">{t.score}</span>
                </div>
                <div className="h-4 rounded-full lq-card overflow-hidden">
                  <div className="h-full lq-grow" style={{ width: `${(t.score / tmax) * 100}%`, background: 'var(--accent)', transformOrigin: 'left' }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
  const [first, second, third] = rows;
  const col = (r: (typeof rows)[number] | undefined, h: string, place: number) =>
    r ? (
      <div className="flex flex-col items-center justify-end lq-rise">
        <div className="text-3xl font-bold mb-2">{r.emoji} {r.nickname}</div>
        <div className={`w-40 ${h} rounded-t-2xl flex items-start justify-center pt-3`} style={{ background: 'var(--accent)', color: 'var(--accent-fg)' }}>
          <span className="text-6xl font-black lq-display">{place}</span>
        </div>
        <div className="mt-2 text-2xl tabular-nums">{r.score}</div>
      </div>
    ) : (
      <div className="w-40" />
    );

  return (
    <div className="h-full flex flex-col items-center justify-center gap-10">
      <h1 className="text-6xl font-black lq-display">{t('final')}</h1>
      <div className="flex items-end gap-6">
        {col(second, 'h-40', 2)}
        {col(first, 'h-56', 1)}
        {col(third, 'h-28', 3)}
      </div>
      {p.hardest && p.hardest.length > 0 && (
        <div className="text-center lq-muted">
          <div className="text-lg mb-1">{t('hardestQuestion')}</div>
          <div className="text-xl">{t('hardestLine', { prompt: p.hardest[0]!.prompt, pct: Math.round(p.hardest[0]!.correctRatio * 100) })}</div>
        </div>
      )}
    </div>
  );
}

// ─── Steuerleiste ────────────────────────────────────────────────────────────

/** Glücksrad-Folie: dreht auf Knopfdruck und landet zufällig auf einem Eintrag. */
function SpinnerView({ entries }: { entries: string[] }) {
  const t = useT();
  const [idx, setIdx] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const [winner, setWinner] = useState<number | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  function spin() {
    if (spinning || entries.length === 0) return;
    setWinner(null);
    setSpinning(true);
    const target = Math.floor(Math.random() * entries.length);
    let delay = 60;
    let elapsed = 0;
    let cur = idx;
    const tick = () => {
      cur = (cur + 1) % entries.length;
      setIdx(cur);
      elapsed += delay;
      delay = Math.min(430, delay * 1.13);
      if (elapsed >= 2800) { setIdx(target); setWinner(target); setSpinning(false); burstConfetti(); return; }
      timer.current = setTimeout(tick, delay);
    };
    timer.current = setTimeout(tick, delay);
  }

  if (entries.length === 0) {
    return <div className="text-3xl lq-muted">{t('noSpinnerEntries')}</div>;
  }
  return (
    <div className="flex flex-col items-center gap-8">
      <div className={`text-7xl font-black lq-display px-10 py-6 rounded-3xl lq-card lq-border ${winner !== null ? 'lq-pop' : ''}`} style={{ color: winner !== null ? 'var(--accent)' : undefined, minWidth: '50vw', textAlign: 'center' }}>
        {entries[idx]}
      </div>
      <button onClick={spin} disabled={spinning} className="rounded-2xl px-10 py-4 text-3xl font-bold disabled:opacity-50" style={{ background: 'var(--accent)', color: 'var(--accent-fg)' }}>
        {spinning ? t('spinning') : winner !== null ? t('spinAgain') : t('spin')}
      </button>
    </div>
  );
}

/** Zufalls-Ziehung eines Teilnehmers („Wer ist dran?") mit kurzem Spin + Konfetti. */
function RandomPicker({ participants, onClose }: { participants: LobbyParticipant[]; onClose: () => void }) {
  const [idx, setIdx] = useState(0);
  const [done, setDone] = useState(false);
  useEffect(() => {
    if (participants.length === 0) return;
    const winner = Math.floor(Math.random() * participants.length);
    let delay = 60;
    let elapsed = 0;
    let cur = 0;
    let timer: ReturnType<typeof setTimeout>;
    const tick = () => {
      cur = (cur + 1) % participants.length;
      setIdx(cur);
      elapsed += delay;
      delay = Math.min(420, delay * 1.13);
      if (elapsed >= 2600) {
        setIdx(winner);
        setDone(true);
        burstConfetti();
        return;
      }
      timer = setTimeout(tick, delay);
    };
    timer = setTimeout(tick, delay);
    return () => clearTimeout(timer);
  }, [participants.length]);

  const person = participants[idx];
  const t = useT();
  return (
    <div className="fixed inset-0 z-50 bg-black/75 flex flex-col items-center justify-center px-6 text-center" onClick={onClose}>
      <div className="text-3xl lq-muted mb-8">{done ? t('selected') : t('whoseTurn')}</div>
      <div className={`text-8xl mb-4 ${done ? 'lq-pop' : ''}`}>{person?.emoji ?? '🎲'}</div>
      <div className="text-6xl font-extrabold lq-display" style={{ color: '#fff' }}>{person?.nickname ?? '—'}</div>
      <button className="mt-12 rounded-lg lq-card px-5 py-2 text-lg" onClick={onClose}>{t('close')}</button>
    </div>
  );
}

const MUSIC_BED_KEY = 'lq_presenter_music_bed';

function ControlBar({ p, scale, setScale, onRandom }: { p: PresenterState; scale: number; setScale: (fn: (s: number) => number) => void; onRandom: () => void }) {
  const t = useT();
  const [pack, setPackState] = useState<SoundPack>(getPack());
  const [musicBed, setMusicBedState] = useState<MusicBed>(getMusicBed());
  const btn = 'rounded-lg lq-card hover:opacity-80 px-3 py-2 text-sm font-medium disabled:opacity-40';

  // Zuletzt gewählte Musik-Bibliothek wiederherstellen (Beamer-lokal).
  useEffect(() => {
    try {
      const saved = localStorage.getItem(MUSIC_BED_KEY) as MusicBed | null;
      if (saved && saved !== getMusicBed()) {
        setMusicBed(saved);
        setMusicBedState(saved);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="shrink-0 lq-border border-t bg-black/30 px-4 py-2 flex items-center gap-2">
      <span className={`w-2.5 h-2.5 rounded-full ${p.connected ? 'bg-green-400' : 'bg-red-400'}`} title={p.connected ? t('connected') : t('disconnected')} />
      {p.state === 'lobby' ? (
        <button className={btn} onClick={() => p.act('host:start')}>{t('start')}</button>
      ) : (
        <button className={btn} onClick={() => p.act('host:next')}>{t('nextSpace')}</button>
      )}
      <button className={btn} disabled={p.phase !== 'answering'} onClick={() => p.act('host:skip')}>{t('skip')}</button>
      <button className={btn} disabled={p.phase !== 'answering'} onClick={() => p.extend(10)}>+10s</button>
      <button className={btn} onClick={() => p.act(p.paused ? 'host:resume' : 'host:pause')}>{p.paused ? t('resume') : t('pauseEsc')}</button>
      <button className={btn} onClick={() => { if (confirm(t('endConfirm'))) p.act('host:end'); }}>{t('end')}</button>
      <button className={btn} disabled={p.lobby.length === 0} onClick={onRandom} title={t('drawRandom')}>🎲 {t('random')}</button>
      <label className="flex items-center gap-1 text-sm">
        <span>{pack === 'off' ? '🔈' : '🔊'}</span>
        <select
          className="rounded-lg lq-card px-2 py-2 text-sm"
          value={pack}
          onChange={(e) => { const v = e.target.value as SoundPack; setPack(v); setPackState(v); }}
          title={t('soundEffects')}
        >
          <option value="off">{t('effectsOff')}</option>
          <option value="arcade">{t('arcade')}</option>
          <option value="sanft">{t('soft')}</option>
        </select>
      </label>
      <label className="flex items-center gap-1 text-sm">
        <span>🎵</span>
        <select
          className="rounded-lg lq-card px-2 py-2 text-sm"
          value={musicBed}
          onChange={(e) => {
            const v = e.target.value as MusicBed;
            setMusicBed(v);
            setMusicBedState(v);
            try { localStorage.setItem(MUSIC_BED_KEY, v); } catch { /* ignore */ }
            // Musik sofort starten, wenn gerade eine Frage läuft.
            wantMusic(p.phase === 'countdown' || p.phase === 'answering');
          }}
          title={t('musicLib')}
        >
          {MUSIC_BEDS.map((b) => (
            <option key={b.id} value={b.id} title={b.description}>{b.label === 'Musik aus' ? t('musicOff') : b.label}</option>
          ))}
        </select>
      </label>
      <div className="ml-auto flex items-center gap-1">
        <span className="text-xs lq-muted">{t('font')}</span>
        <button className={btn} onClick={() => setScale((s) => Math.max(70, s - 10))}>−</button>
        <span className="text-xs tabular-nums w-10 text-center">{scale}%</span>
        <button className={btn} onClick={() => setScale((s) => Math.min(160, s + 10))}>+</button>
      </div>
    </div>
  );
}
